import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { JobStatus } from '../../../../enums/job-status';
import { SelectItem } from 'primeng';
import { Panel } from 'primeng/panel/panel';
import { Subscription } from 'rxjs';
import { EventService, JobsService, MapService } from '../../../../services';
import { JobScreenCommonService } from '../../common/job-screen-common.service';
import {
  ISupplierJobScreenTasks,
  ISupplierJobScreenTasksTaskInfo,
} from '../../../../interfaces/screen-specific/job-screen/supplier/supplier-job-screen-tasks';
import { OrganisationTypeEnum } from '../../../../enums/organisation-types';
import { TravelSchedulerComponent } from '../../../travel-scheduler/travel-scheduler.component';

@Component({
  selector: 'supplier-job-screen-job-tasks',
  templateUrl: './supplier-job-screen-job-tasks.component.html',
  styleUrls: [
    './supplier-job-screen-job-tasks.component.scss',
    '../../common/job-screen-common.scss',
    '../../common/common-scss/job-screen-tasks-common.scss',
  ],
})
export class SupplierJobScreenJobTasksComponent implements OnChanges, OnDestroy {
  public jobStatus = JobStatus;

  @Output() editSupplierSchedule = new EventEmitter();

  @Input() hideTaskSchedule: boolean = false;

  @Input() organisationId: number;
  @Input() jobId: number;

  @ViewChild('panel') panel: Panel;

  organisationTypeId: number = OrganisationTypeEnum.Supplier;

  selectedTaskId: number;

  selectedTask: ISupplierJobScreenTasksTaskInfo;

  public taskSequencingEnabled: boolean = false;
  protected editTaskScheduleVisible: boolean = false;

  public get secondaryVehicleInfo(): string[] {
    if (!this.selectedTask) {
      return [];
    }

    const fields = [
      this.selectedTask.vehicle.color,
      this.selectedTask.vehicle.kerbWeight ? `${this.selectedTask.vehicle.kerbWeight}kg` : null,
      this.selectedTask.vehicle.fuelType,
    ];

    return fields.filter((x) => x != null && x != '');
  }

  public tasks: SelectItem[];

  public job: ISupplierJobScreenTasks;

  routeInfo: { distanceMiles: number; time: string };
  interactiveMap: boolean = false;
  staticMapSrc: string;
  mapWidth: number;

  subscriptions: Subscription[] = [];

  constructor(
    private mapService: MapService,
    private jobService: JobsService,
    private eventService: EventService,
    public commonService: JobScreenCommonService,
  ) {
    this.subscriptions.push(
      this.eventService.JobScreenEvent.subscribe((event) => {
        this.handleEvents(event);
      }),
    );
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.jobId && this.jobId != null) {
      this.selectedTaskId = null;
      void this.fetchJob();
    }
  }

  handleEvents(event: any) {
    if (event.event == 'task-changed') {
      this.selectedTaskId = event.data.taskId;
      this.updateSelectedTask();
    }

    if (event.event == 'refresh-job') {
      void this.fetchJob();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((x) => x?.unsubscribe());
  }

  changeSelectedTask(taskId: number) {
    this.eventService.JobScreenEvent.emit({
      event: 'task-changed',
      data: { taskId, jobId: this.job.jobId },
      sender: 'changeSelectedTask',
    });
  }

  updateSelectedTask() {
    this.selectedTask = this.job.tasks.find((o) => o.id == this.selectedTaskId);

    if (this.selectedTask == null) {
      return;
    }

    // @ts-ignore
    const mapWidth = Math.round(this.panel.el.nativeElement.offsetWidth * 0.4);
    this.mapWidth = mapWidth;

    this.staticMapSrc = this.mapService.getStaticMapWithMarkers([this.selectedTask], {
      selectedTaskId: this.selectedTaskId,
      mapSize: `${mapWidth}x398`,
    });

    this.updateRouteInfo();

    if (this.selectedTask.taskTracking.length == 0) {
      this.loadTaskTrackingForTask(this.selectedTask.id);
    }
  }

  refreshJob() {
    this.tasks = [];

    if (this.job) {
      this.job.tasks.forEach((item) => {
        this.tasks.push({ label: item.vehicle.regNumber, value: item });
      });

      this.selectedTask = this.job.tasks.find((o) => o.id == this.selectedTaskId);
      if (this.selectedTaskId == null) {
        this.changeSelectedTask(this.job.tasks[0].id);
      }

      this.taskSequencingEnabled = this.job.tasks.some(
        (task) => task.orderSequence != null || task.taskSequence != null,
      );
    }
  }

  updateRouteInfo() {
    this.routeInfo = { distanceMiles: null, time: null };

    // convert timespan string to seconds
    if (this.selectedTask.estimateTime) {
      this.routeInfo.time = this.mapService.getTravelTimeFormatted(this.selectedTask.estimateTime);
      this.routeInfo.distanceMiles = this.mapService.getMiles(this.selectedTask.distance);
    }
  }

  loadTaskTrackingForTask(taskId: number): void {
    this.jobService.getTaskTrackingInfo(this.job.jobId, taskId).then((taskTrackingInfo) => {
      if (taskTrackingInfo && taskTrackingInfo.length > 0) {
        this.selectedTask = {
          ...this.selectedTask,
          taskTracking: taskTrackingInfo,
          currentLocation: taskTrackingInfo[taskTrackingInfo.length - 1],
        };
        this.updateRouteInfo();
      }
    });
  }

  async fetchJob() {
    this.job = await this.jobService.getForComponent<ISupplierJobScreenTasks>(
      this.jobId,
      'supplier-job-screen-tasks',
    );
    this.refreshJob();
  }

  onEditSupplierScheduleClick() {
    this.editTaskScheduleVisible = true;
  }

  closeTravelScheduler() {
    this.editTaskScheduleVisible = false;
  }

  handleScheduleCompleted() {
    this.closeTravelScheduler();
    location.reload();
  }

  protected isScheduleMissing(): boolean {
    return this.job.tasks.some(
      (x) =>
        x.taskSupplierSchedule?.specifiedPickUpTime == null ||
        x.taskSupplierSchedule?.specifiedDropOffTime == null,
    );
  }

  protected getEditScheduleButtonLabel() {
    let scheduleMissing = this.isScheduleMissing();

    if (scheduleMissing) {
      return 'Add Schedule';
    }
    return 'Edit Schedule';
  }
}
